import { useEffect, useState, useCallback } from 'react';
import { VoiceNavigation } from '@components/VoiceNavigation';
import { LandingPage } from '@components/LandingPage';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { HazardNotificationProvider } from './contexts/HazardNotificationContext';
import { telegram } from '@services/telegram';
import { loadGoogleMaps } from '@services/googleMaps';
import { useAppStore } from '@stores/appStore';

// Store original fetch globally for offline simulation
const originalFetch = window.fetch.bind(window);
(window as any).__originalFetch = originalFetch;

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const simulateOffline = useAppStore((state) => state.simulateOffline);
  
  // Install/update fetch interceptor when simulateOffline changes
  useEffect(() => {
    const origFetch = (window as any).__originalFetch || originalFetch;
    
    if (simulateOffline) {
      // Override fetch to simulate network failure
      window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
        
        // Allow some essential requests through
        if (url.includes('chrome-extension://') || url.startsWith('blob:') || url.startsWith('data:')) {
          return origFetch(input, init);
        }
        
        console.log('🚫 [OFFLINE SIMULATION] Blocked request:', url);
        
        // Simulate network error with a slight delay to feel realistic
        await new Promise(r => setTimeout(r, 100 + Math.random() * 200));
        throw new TypeError('Failed to fetch (simulated offline mode)');
      };
      
      console.log('📴 [OFFLINE SIMULATION] Enabled - all API requests will fail');
    } else {
      // Restore original fetch
      window.fetch = origFetch;
      console.log('📶 [OFFLINE SIMULATION] Disabled - normal network restored');
    }
  }, [simulateOffline]);

  // Stable auth handler that can be used as global fallback
  const handleTelegramAuth = useCallback((user: any) => {
    console.log('handleTelegramAuth called with:', JSON.stringify(user));
    
    // Accept any valid user object from Telegram
    if (user) {
      const userId = user.id || user.user?.id;
      const firstName = user.first_name || user.user?.first_name || 'User';
      
      if (userId) {
        console.log('Telegram login successful:', firstName, 'ID:', userId);
        setIsAuthenticated(true);
        return;
      }
    }
    
    console.error('Invalid user data from Telegram:', user);
    alert('Authentication failed. Please try again.');
  }, []);

  useEffect(() => {
    // Set up global fallback callback for Telegram Login Widget
    // This catches auth even if component callbacks fail
    (window as any).TelegramLoginCallback = handleTelegramAuth;
    
    // Initialize Telegram WebApp
    telegram.initialize();
    
    // Load Google Maps API
    loadGoogleMaps().catch(err => {
      console.warn('Google Maps failed to load:', err);
    });

    // Check for shared route URL parameter (?route=123)
    const urlParams = new URLSearchParams(window.location.search);
    const routeParam = urlParams.get('route');
    if (routeParam) {
      const routeId = parseInt(routeParam, 10);
      if (!isNaN(routeId)) {
        console.log('Shared route detected:', routeId);
        useAppStore.getState().setSharedRoutePhotoId(routeId);
      }
    }

    // Check for shared facility URL parameter (?facility=xxx)
    const facilityParam = urlParams.get('facility');
    if (facilityParam) {
      console.log('Shared facility detected:', facilityParam);
      useAppStore.getState().setSharedFacilityId(facilityParam);
    }

    // Clean the URL if any params were present
    if (routeParam || facilityParam) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Check if already authenticated via Telegram WebApp
    const user = telegram.getUser();
    if (user) {
      setIsAuthenticated(true);
      
      // Check for start parameter (e.g., nearby_37.5_-107.8)
      const startParam = telegram.getStartParam();
      if (startParam && startParam.startsWith('nearby_')) {
        const parts = startParam.split('_');
        if (parts.length === 3) {
          const lat = parseFloat(parts[1]);
          const lng = parseFloat(parts[2]);
          // Store location for VoiceNavigation to use
          useAppStore.getState().setNearbyLocation({ lat, lng });
        }
      }
      
      setIsLoading(false);
    } else {
      // Need Telegram Login Widget
      setIsLoading(false);
    }
    
    return () => {
      delete (window as any).TelegramLoginCallback;
    };
  }, [handleTelegramAuth]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#0f172a',
        flexDirection: 'column'
      }}>
        <style>
          {`
            @keyframes cyber-spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes cyber-color {
              0% { border-top-color: #60a5fa; box-shadow: 0 0 20px #60a5fa40; }
              25% { border-top-color: #34d399; box-shadow: 0 0 20px #34d39940; }
              50% { border-top-color: #fbbf24; box-shadow: 0 0 20px #fbbf2440; }
              75% { border-top-color: #f472b6; box-shadow: 0 0 20px #f472b640; }
              100% { border-top-color: #60a5fa; box-shadow: 0 0 20px #60a5fa40; }
            }
            @keyframes cyber-pulse {
              0%, 100% { opacity: 0.6; transform: scale(1); }
              50% { opacity: 1; transform: scale(1.05); }
            }
          `}
        </style>
        <div style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          border: '4px solid rgba(96, 165, 250, 0.2)',
          borderTopColor: '#60a5fa',
          animation: 'cyber-spin 1s linear infinite, cyber-color 3s ease-in-out infinite',
          marginBottom: 16
        }} />
        <p style={{
          color: '#94a3b8',
          fontSize: '1.1rem',
          fontWeight: 500,
          animation: 'cyber-pulse 2s ease-in-out infinite'
        }}>
          Loading ZiaWellNav...
        </p>
      </div>
    );
  }

  // Show Telegram Login if not authenticated via WebApp
  if (!isAuthenticated) {
    return (
      <ErrorBoundary>
        <LandingPage onAuth={handleTelegramAuth} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <HazardNotificationProvider enabled={true} pollInterval={30000}>
        <div className="min-h-screen bg-tg-bg">
          <VoiceNavigation />
        </div>
      </HazardNotificationProvider>
    </ErrorBoundary>
  );
}

export default App;
